import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MonacoEditor, { OnMount } from "@monaco-editor/react";
import type * as monacoTypes from "monaco-editor/esm/vs/editor/editor.api";

import {
  installSnipToggle,
  registerSnip,
  renderSnipTokenBackgrounds
} from "@/zapi-admin/shared/components/MonacoTabs/lib/snip";
import { Resolver } from "../model/types";

export type TFileTab = { id: string; label: string; value: string; language?: string };

export function MonacoTabs({
  files,
  dataMap,
  execMap
}: {
  files: TFileTab[];
  // TODO this could set per file
  execMap?: Record<string, string>;
  dataMap?: Record<string, string>;
}) {
  const [activeId, setActiveId] = useState(files[0]?.id ?? "");
  const models = useRef(new Map<string, monacoTypes.editor.ITextModel>());
  const viewStates = useRef(new Map<string, monacoTypes.editor.ICodeEditorViewState | null>());
  const editorRef = useRef<monacoTypes.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof monacoTypes | null>(null);

  const toggleDisposeRef = useRef<{ dispose: () => void } | null>(null);
  const contentDisposeRef = useRef<monacoTypes.IDisposable | null>(null);

  const innSnipResolver = useMemo<Resolver>(
    () => ({
      path: (full) => dataMap?.[full] ?? "(missing)",
      include: (name) => dataMap?.[name] ?? "(missing)",
      exec: (name) => execMap?.[name] ?? "(missing)"
    }),
    [dataMap, execMap]
  );

  const bindForModel = useCallback(
    (editor: monacoTypes.editor.IStandaloneCodeEditor, monaco: typeof monacoTypes) => {
      toggleDisposeRef.current?.dispose?.();
      toggleDisposeRef.current = installSnipToggle(editor, monaco, innSnipResolver);

      contentDisposeRef.current?.dispose?.();
      let t: NodeJS.Timeout | undefined;
      contentDisposeRef.current = editor.onDidChangeModelContent(() => {
        clearTimeout(t);
        t = setTimeout(() => renderSnipTokenBackgrounds(editor), 80);
      });
      renderSnipTokenBackgrounds(editor);
    },
    [innSnipResolver]
  );

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    registerSnip(monaco, innSnipResolver);

    // Create models for all tabs
    for (const f of files) {
      if (models.current.has(f.id)) continue;
      const uri = monaco.Uri.parse(`inmemory://${f.id}`);
      const model = monaco.editor.createModel(f.value, f.language, uri);
      models.current.set(f.id, model);
    }

    // Set initial model
    const m = models.current.get(activeId) ?? models.current.values().next().value;
    if (m) {
      editor.setModel(m);
    }

    bindForModel(editor, monaco);
    editor.onDidChangeModel(() => {
      bindForModel(editor, monaco);
    });
  };

  // Switch tabs
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;

    // save current view state
    const currentModel = editor.getModel();
    if (currentModel) {
      const prevId = [...models.current.entries()].find(([, m]) => m === currentModel)?.[0];
      if (prevId) viewStates.current.set(prevId, editor.saveViewState());
    }

    // switch to next model
    const nextModel = models.current.get(activeId);
    if (nextModel) {
      editor.setModel(nextModel);
      const vs = viewStates.current.get(activeId);
      if (vs) editor.restoreViewState(vs);
      editor.focus();
      // rebind handlers for the new model
      bindForModel(editor, monaco);
    }
  }, [activeId, files, bindForModel]);

  // Keep models in sync with incoming `files` prop
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;

    // add/update
    files.forEach((f) => {
      const uri = monaco.Uri.parse(`inmemory://${f.id}`);
      let model = models.current.get(f.id);
      if (!model) {
        model = monaco.editor.createModel(f.value, f.language, uri);
        models.current.set(f.id, model);
      } else {
        if (model.getValue() !== f.value) model.setValue(f.value);
      }
    });

    // remove stale models
    for (const [id, model] of models.current) {
      if (!files.some((f) => f.id === id)) {
        model.dispose();
        models.current.delete(id);
        viewStates.current.delete(id);
      }
    }

    // fix activeId if its tab disappeared
    if (!files.some((f) => f.id === activeId)) {
      setActiveId(files[0]?.id ?? "");
    }
  }, [files, activeId]);

  // Cleanup
  useEffect(() => {
    const current = models.current;
    return () => {
      toggleDisposeRef.current?.dispose?.();
      contentDisposeRef.current?.dispose?.();
      for (const m of current.values()) m.dispose();
      current.clear();
    };
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex gap-2 border-b px-2 py-1">
        {files.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveId(f.id)}
            className={`px-3 py-1 rounded-t ${f.id === activeId ? "bg-gray-200" : "hover:bg-gray-100"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0">
        <MonacoEditor
          onMount={onMount}
          theme="light"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            wordWrap: "on",
            // wordWrapColumn: 120,
            scrollBeyondLastLine: false,
            renderWhitespace: "all",
            automaticLayout: true,
            stickyScroll: { enabled: false }
          }}
        />
      </div>
    </div>
  );
}
